import { concat, of, EMPTY } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { GuidService } from 'ui-components/services/guid-service';
import * as EntitiesActions from './entities.actions';
import { getActualSalesOrderIds, getIsOrdersPage } from '../orders/orders.helpers';
import Global from '../../store/global-selectors';
import * as FulfillmentPlansApi from '../../fulfillment-plans/fulfillment-plans.api';
import { bulkLoadSalesOrders$ } from '../bulkload/bulkload.observables';
import * as AddressApi from '../../address/address.api';
import * as FraudIndicatorsApi from '../../fulfillment-plans/fraud-indicators.api';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import * as bulkActions from '../bulkload/bulkload.actions';
import { Segment } from '../../services/analytics-services/segment';
import { FRAUD_INDICATOR_DISMISSED } from '../../constants/analytics-events';
import { silentlyGetOrderDetails } from '../orders/order-details/orders.details.actions';

const messages = defineMessages({
  unlockAddressErrorTitle: {
    description: 'Unlock address error title',
    defaultMessage: 'Failed to Unlock Address',
    id: 'xkR7nY',
  },
  unlockAddressErrorContent: {
    description: 'Unlock address error description',
    defaultMessage: 'An error occurred while unlocking this address. Please try again.',
    id: 'vBpHtX',
  },
  dismissFraudIndicatorSuccess: {
    description: 'Success message when fraud indicator is dismissed',
    defaultMessage: 'Fraud indicator dismissed',
    id: '8Fxqvn',
  },
  dismissFraudIndicatorError: {
    description: 'Error message when fraud indicator dismissal fails',
    defaultMessage: 'Failed to dismiss fraud indicator',
    id: 'VG7by9',
  },
});

export const bulkFetchOrderDetails = (action$, { getState }) => {
  return action$.pipe(
    ofType(EntitiesActions.BULK_LOAD_ENTITIES_REQUEST),
    switchMap((action) => {
      const state = getState();
      const requestTime = new Date();
      let salesOrderIds;

      const { salesOrderIds: entities } = action.payload;
      const isOrdersPage = getIsOrdersPage();
      const areEntitiesStrings = entities.every((entity) => typeof entity === 'string');

      // Depending on where this request is originating, this may either be an actual object record *or* just a string id value
      if (!isOrdersPage && !areEntitiesStrings) {
        salesOrderIds = entities.map((salesOrder) => salesOrder && salesOrder.salesOrderId).filter((salesOrderId) => salesOrderId);
      } else {
        const selectedEntityIds = Global.selectSelectedOrdersIds(state);
        const selectedSalesOrderIds = getActualSalesOrderIds(state, selectedEntityIds) || [];
        salesOrderIds = [...entities.filter((salesOrderId) => salesOrderId), ...selectedSalesOrderIds];
      }

      if (!salesOrderIds || salesOrderIds.length === 0) {
        return EMPTY;
      }

      const actualSalesOrderIds = getActualSalesOrderIds(getState(), salesOrderIds);

      const requestId = GuidService.newGuid();
      return concat(
        of(EntitiesActions.saveBulkRequestMetadata({ requestId, requestTime })),
        bulkLoadSalesOrders$(actualSalesOrderIds).pipe(
          mergeMap((bulkloadSuccessAction) =>
            concat(
              of(bulkloadSuccessAction),
              of(
                EntitiesActions.bulkRequestEntitiesSuccess({
                  salesOrderIds: actualSalesOrderIds,
                }),
              ),
            ),
          ),
          catchError(() => EMPTY),
        ),
      );
    }),
  );
};

export const updateFulfillmentPlanNote = (action$) => {
  return action$.pipe(
    ofType(EntitiesActions.UPDATE_FULFILLMENT_PLAN_NOTE_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanId, note } = action.payload;

      return FulfillmentPlansApi.updateFulfillmentPlanNote(fulfillmentPlanId, note).pipe(
        map(action.meta.onSuccess),
        catchError((error) => {
          return of(action.meta.onError(error));
        }),
      );
    }),
  );
};

export const unlockAddress = (action$) => {
  return action$.pipe(
    ofType(EntitiesActions.UNLOCK_ADDRESS_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanIds } = action.payload;
      return AddressApi.unlockAddress(fulfillmentPlanIds).pipe(
        mergeMap(() => {
          return of(action.meta.onSuccess(), bulkActions.bulkloadFulfillmentPlans(fulfillmentPlanIds));
        }),
        catchError((error) => {
          return of(action.meta.onError(error));
        }),
      );
    }),
  );
};

export const unlockAddressFailure = (action$) => {
  return action$.pipe(
    ofType(EntitiesActions.UNLOCK_ADDRESS_FAILURE),
    mergeMap(() => {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.unlockAddressErrorTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...messages.unlockAddressErrorContent} />,
      });
      return EMPTY;
    }),
  );
};

export const dismissFraudIndicator = (action$, { getState }) => {
  return action$.pipe(
    ofType(EntitiesActions.DISMISS_FRAUD_INDICATOR_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanId, fraudIndicatorCode, indicator } = action.payload;

      return FraudIndicatorsApi.dismissFraudIndicator(fulfillmentPlanId, fraudIndicatorCode).pipe(
        mergeMap(() => {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            title: <FormattedMessage {...messages.dismissFraudIndicatorSuccess} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });

          if (indicator) {
            Segment.trackEvent(FRAUD_INDICATOR_DISMISSED, {
              fraudIndicatorCode: indicator.fraudIndicatorCode,
              fraudIndicatorName: indicator.fraudIndicatorName,
              category: indicator.category,
              severity: indicator.severity,
              blockingBehavior: indicator.blockingBehavior,
            });
          }

          // Get the salesOrderId from the fulfillmentPlan to refetch order details
          const state = getState();
          const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId });
          const salesOrderId = fulfillmentPlan?.salesOrdersFulfilledIds?.[0];

          const actions = [action.meta.onSuccess()];

          // Refetch order details to get updated hold status
          if (salesOrderId) {
            actions.push(silentlyGetOrderDetails(salesOrderId));
          }

          return of(...actions);
        }),
        catchError((error) => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.dismissFraudIndicatorError} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            duration: NOTIFICATION_DURATIONS.MEDIUM,
          });

          return of(action.meta.onError(error));
        }),
      );
    }),
  );
};
